import React, { FC, useCallback, useEffect, useState } from 'react';
import ToggleButtonGroupContext from './ToggleButtonGroupContext';

export type Toggles = (string | number)[];

export type ToggleButtonGroupProps = {
  children?: React.ReactNode;
  /**
   * name of the ToggleButtonGroup
   */
  name?: string;

  /**
   * enable multi sections
   */
  multi?: boolean;

  /**
   * enable multi sections
   */
  initialToggles?: Toggles;

  /**
   * event handler called when value changes
   */
  onChange?(toggles: Toggles): void;
};

export const ToggleButtonGroup: FC<ToggleButtonGroupProps> = ({
  multi = false,
  onChange,
  children,
  initialToggles,
}: ToggleButtonGroupProps) => {
  const [toggles, setToggles] = useState<Toggles>([]);

  useEffect(() => {
    if (!multi && toggles.length > 1) {
      setToggles([toggles[toggles.length - 1]]);
    }
  }, [multi, toggles]);

  useEffect(() => {
    if (initialToggles) {
      setToggles(initialToggles);
    }
  }, [initialToggles]);

  useEffect(() => {
    if (onChange) {
      onChange(toggles);
    }
  }, [toggles, onChange]);

  const onGroupToggle = useCallback(
    (on: boolean, value: string | number) => {
      if (multi) {
        setToggles(
          on ? [...toggles, value] : toggles.filter((v) => v !== value),
        );
      } else {
        setToggles(on ? [value] : []);
      }
    },
    [toggles, multi],
  );

  const isGroupOn = useCallback(
    (value: string | number) => toggles.indexOf(value) >= 0,
    [toggles],
  );

  return (
    <ToggleButtonGroupContext.Provider
      value={{ onGroupToggle, isGroupOn }}
      data-testid={'ToggleButtonGroupContext.Provider'}
    >
      {children}
    </ToggleButtonGroupContext.Provider>
  );
};
